import { Box, Button, Autocomplete as MUIAutocomplete, TextField } from '@mui/material';
import { useField, Field, FieldProps } from 'formik';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PhoneInputComponent from 'react-phone-input-2';

import { useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, PhoneInput } from '../../types/form-fields';
import { CheckboxField, FormRow } from '../fields';
import { FormBlock } from './common';
import { CountryType, countries } from './utils';

export const isPhoneInputField = (field: FormField): field is PhoneInput => {
  return field.type === FieldType.PhoneInput;
};

export const defaultPhoneInputFieldProps: PhoneInput = {
  type: FieldType.PhoneInput,
  required: true,
  defaultCountry: 'us',
  name: 'phone',
};

type PhoneInputFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

type AutocompleteProps = FieldProps<string> & {
  label: string;
  multiple?: boolean;
  helperText?: ReactNode;
};

const Autocomplete: FC<AutocompleteProps> = ({ form, field, label, multiple, helperText }) => {
  const handleChange = useCallback(
    (event, value) => {
      if (!value) {
        return;
      }

      if ('length' in value) {
        const strValue = (value as CountryType[]).map(({ code }) => code.toLowerCase()).join(',');
        form.setFieldValue(field.name, strValue);
      } else {
        form.setFieldValue(field.name, value.code.toLowerCase());
      }
    },
    [field.name, form]
  );

  const handleBlur = useCallback(
    (event) => {
      field.onBlur(event);
      form.submitForm();
    },
    [field, form]
  );

  const getOptionLabel = useCallback((option) => option.label, []);

  const renderOption = useCallback((props, option) => {
    return (
      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
        <img
          loading="lazy"
          width="20"
          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
          alt=""
        />
        {option.label} ({option.code}) +{option.phone}
      </Box>
    );
  }, []);

  const renderInput = useCallback(
    (opts) => {
      return (
        <TextField
          {...opts}
          label={label}
          inputProps={{
            ...opts.inputProps,
            autoComplete: 'off',
          }}
          helperText={helperText}
        />
      );
    },
    [helperText, label]
  );

  const defaultValue = useMemo(() => {
    if (multiple) {
      const initialValues = (field.value || '').split(',');

      return countries.filter((country) => {
        return initialValues.includes(country.code.toLowerCase());
      });
    }

    return countries.find((country) => country.code.toLowerCase() === field.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MUIAutocomplete
      multiple={multiple}
      options={countries}
      autoHighlight
      size="small"
      defaultValue={defaultValue}
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      onBlur={handleBlur}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );
};

const PhoneInputFieldSettings: FC<PhoneInputFieldSettingsProps> = ({ name, remove }) => {
  const { t } = useTranslation();
  return (
    <PhoneInputFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.defaultCountry`}
          component={Autocomplete}
          label={t('pages.campaign.checkboxField.defaultPhone')}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.onlyCountries`}
          component={Autocomplete}
          label={t('pages.campaign.checkboxField.onlySpecialCountries')}
          helperText={t('pages.campaign.checkboxField.helperText')}
          multiple
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.preferredCountries`}
          component={Autocomplete}
          label={t('pages.campaign.checkboxField.preferredCountries')}
          helperText={t('pages.campaign.checkboxField.preferredCountriesInfo')}
          multiple
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.excludeCountries`}
          component={Autocomplete}
          label={t('pages.campaign.checkboxField.excludeCountries')}
          multiple
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.required`} component={CheckboxField} label={t('pages.campaign.checkboxField.required')} />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </PhoneInputFieldSettingsWrapper>
  );
};

type PhoneInputFieldProps = {
  name: string;
  remove?: () => void;
};

export const PhoneInputField: FC<PhoneInputFieldProps> = ({ name, remove }) => {
  const [field] = useField<PhoneInput>(name);
  const dispatch = useAppDispatch();

  const {
    value: { defaultCountry },
  } = field;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <PhoneInputFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <Cover />

      <PhoneInputComponent specialLabel="" country={defaultCountry.toLowerCase()} />
    </FormBlock>
  );
};

const PhoneInputFieldSettingsWrapper = styled.div`
  max-width: 290px;
`;

const Cover = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  cursor: pointer;
`;
